<template>
  <div v-show="selectedMarket" class="market-selector">
    <h3 v-if="mq('small')" class="market-selector__header">
      {{ tStatic('chooseMarketAndLanguageLabel') }}
    </h3>

    <div class="market-selector__content">
      <div class="market-selector__column">
        <div class="market-selector__column-header">
          {{ tStatic('labelCountry') }}
        </div>
        <div v-for="(item, row) in markets" :key="`market-${row}`">
          <div class="market-selector__column-market-markets">
            <oie-base-radio
              :id="`market-${row}`"
              v-model="selectedMarketValue"
              name="market-country"
              :value="item.value"
              :is-checked="selectedMarket.value === item.value"
              css-classes="market-selector__column-item"
              :label-text="item.text"></oie-base-radio>
          </div>
        </div>
      </div>

      <div class="market-selector__column">
        <div class="market-selector__column-header">
          {{ tStatic('labelLanguage') }}
        </div>

        <div v-for="(item, row) in selectedMarket.languages" :key="`language-${row}`">
          <div class="market-selector__column-market-langs">
            <oie-base-radio
              :id="`language-${row}`"
              v-model="selectedLanguageValue"
              v-tooltip="superUserLanguageInfo(item)"
              name="market-language"
              :value="item.value"
              :is-checked="selectedLanguageValue === item.value"
              css-classes="market-selector__column-item"
              :label-text="item.text"></oie-base-radio>
          </div>
        </div>
      </div>
    </div>

    <p v-if="showMarketSwitchWarning" class="market-selector__warning-content">
      {{ tStatic('switchMarketInfoDescription') }}
    </p>

    <oie-base-button
      v-if="hasOnlyChangedLanguage"
      css-classes="button--with-border button--blue market-selector__select-button"
      :text="hasOnlyChangedLanguage ? tStatic('changeLanguageButtonLabel') : tStatic('chooseMarketAndLanguageButtonLabel')"
      @click.native="switchMarketAction()"></oie-base-button>

    <oie-base-button
      v-if="selectedMarketValue !== clientModel.currentMarket"
      css-classes="button--with-border button--blue market-selector__select-button"
      :text="marketSwitchButtonLabel"
      @click.native="setMarketAndLanguage()"></oie-base-button>

    <oie-base-button css-classes="button--ghost market-selector__select-button" :text="tStatic('modalCancel')" @click.native="closeMarketSelector()"></oie-base-button>
  </div>
</template>

<script>
import httpService from './../core/http-service'
import { mapGetters, mapActions } from 'vuex'
import _ from 'lodash'

export default {
  props: {
    markets: {
      type: Array,
      required: true
    },
    referrerUrl: {
      type: String,
      default: ''
    }
  },
  data: () => ({
    selectedMarketValue: null,
    selectedLanguageValue: null,
    isCorrectMarket: true,
    marketUrl: null
  }),
  computed: {
    ...mapGetters('ui', ['isMarketSelectorOpen']),
    selectedMarket() {
      const currentMarket = this.clientModel && this.clientModel.currentMarket
      return _.find(this.markets, m => m.value === (this.selectedMarketValue || currentMarket))
    },
    showMarketSwitchWarning() {
      if (!this.clientModel) return false
      return this.selectedMarketValue !== this.clientModel.currentMarket
    },
    marketSwitchButtonLabel() {
      if (!this.clientModel) return ''
      if (this.clientModel.user && this.clientModel.user.showMarketSwitchLogoutMessage) {
        return this.tStatic('switchMarketContinueAction')
      }
      return this.tStatic('switchMarketContinueAdminsAction')
    },
    hasOnlyChangedLanguage() {
      if (!this.clientModel) return false
      return this.selectedMarketValue === this.clientModel.currentMarket && this.selectedLanguageValue !== this.clientModel.currentLanguage
    }
  },
  watch: {
    selectedMarket() {
      if (!this.clientModel) return

      if (this.selectedMarketValue === this.clientModel.currentMarket) {
        this.selectedLanguageValue = this.clientModel.currentLanguage
      } else {
        const defaultLanguageValueOrFirst = _.chain(this.selectedMarket.languages).find('default').get('value').value() || _.get(this.selectedMarket.languages, '[0].value')
        this.selectedLanguageValue = defaultLanguageValueOrFirst
      }
    }
  },
  mounted() {
    if (this.clientModel) {
      this.selectedMarketValue = this.clientModel.currentMarket
    }

    window.addEventListener('resize', _.throttle(this.resizeCloseMarketSelector, 600))
  },
  methods: {
    ...mapActions('ui', ['toggleValue']),
    switchMarketAction() {
      if (this.hasOnlyChangedLanguage || !(this.clientModel && this.clientModel.isAuthenticated)) {
        this.setMarketAndLanguage()
      }
    },
    setMarketAndLanguage() {
      if(this.selectedMarketValue && this.selectedLanguageValue) {
        this.$store.dispatch('checkout/clearCheckout')
        httpService.post('setMarketAndLanguage', { selectedMarket: this.selectedMarketValue, selectedLanguage: this.selectedLanguageValue, referrerUrl: this.referrerUrl }).then((response) => {
          this.$store.dispatch('chat/resetChat')
          localStorage.removeItem('sessionId')
          this.$store.dispatch('chat/minimizeChatbot')
          window.location.href = response.data.url
        })
      }
    },
    closeMarketSelector() {
      this.toggleValue({ key: 'isMarketSelectorOpen', value: false })
      this.publish('close-market-selector')
    },
    resizeCloseMarketSelector() {
      if (this.mq('to-small') && this.isMarketSelectorOpen) {
        this.closeMarketSelector()
      }
    },
    superUserLanguageInfo(item) {
      if (!this.clientModel.impersonating) { return null }
      let content = null
      if (this.clientModel && this.clientModel.user && item.value === this.clientModel.user.preferredLanguage) {
        content = '<p>Customer preferred language</p>'
      }
      if (this.clientModel && item.value === this.clientModel.impersonating.superUserPreferredLanguage) {
        content = (content || '') + '<p>Super user preferred language</p>'
      }
      if (content) {
        return { content, classes: 'inverted' }
      } else if (this.selectedLanguageValue === item.value) {
        return { content: 'Manually selected language', classes: 'inverted' }
      }
      return null
    }
  }
}
</script>

<style lang="scss">
.market-selector {
  padding: measure(quarter);

  @include media(small) {
    padding: measure(full);
  }
}

.market-selector__header {
  margin: 0 0 measure(half) 0;
}

.market-selector__content {
  display: flex;
}

.market-selector__column {
  flex-basis: 50%;
  min-width: 12.5rem;
  padding-right: measure(half);
}

.navigation-market-selector__content .market-selector__column {
  min-width: initial;
}

.market-selector__column > *,
.market-selector__column-market-langs > * {
  margin-bottom: measure(half);
}

.market-selector__column-header {
  @include body-semi-bold;
}

.market-selector__column-item .radio__label:before {
  border-color: color(blue-2);
}

.market-selector__column-item .radio__element:checked + .radio__label:before {
  background-color: color(blue-2);
  border-color: color(blue-2);
}

.market-selector__select-button {
  width: 100%;
  margin-bottom: measure(quarter);
}
</style>
